import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import PopoverTrigger from './PopoverTrigger';
import OrgCommunitySelector from './OrgCommunitySelector';

export default class Navbar extends Component {
  static propTypes = {
    organizations: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        logo: PropTypes.string.isRequired,
        canAddCommunities: PropTypes.bool,
        communities: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            slug: PropTypes.string.isRequired,
            logo: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
            active: PropTypes.bool,
          })
        ).isRequired,
      })
    ).isRequired,
    activeCommunity: PropTypes.object,
    newCommunityUrl: PropTypes.string,
    nav: PropTypes.shape({
      items: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          active: PropTypes.bool,
          label: PropTypes.string,
          icon: PropTypes.string,
          type: PropTypes.string.isRequired,
        })
      ).isRequired,
    }),
    isStaff: PropTypes.bool.isRequired,
    unionLogo: PropTypes.string.isRequired,
    canOpenSelector: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    activeCommunity: null,
    newCommunityUrl: null,
    nav: {
      activeItem: null,
      items: [],
    },
  };

  constructor(props) {
    super(props);

    this.state = {
      collapsed: this.localStorageValue() || false,
      open: {},
    };
  }

  componentDidMount() {
    this.updateBodyClasses(this.state.collapsed);
    const activeElement = document.querySelector('.navbar-nav-link.active');
    if (activeElement !== null) {
      activeElement.scrollIntoView();
    }
  }

  localStorageValue() {
    return localStorage.getItem('navbarCollapsed') !== null
      ? localStorage.getItem('navbarCollapsed') === 'true'
      : null;
  }

  updateBodyClasses(collapsed) {
    if (collapsed) {
      document.body.classList.remove('f-navbar-expanded-fixed');
      document.body.classList.add('f-navbar-collapsed-fixed');
    } else {
      document.body.classList.add('f-navbar-expanded-fixed');
      document.body.classList.remove('f-navbar-collapsed-fixed');
    }
  }

  handleCollapseTriggerClick = () => {
    const { collapsed } = this.state;
    this.updateBodyClasses(!collapsed);
    this.setState({ collapsed: !collapsed });
    localStorage.setItem('navbarCollapsed', !collapsed);
  };

  renderCollapsedLink = (item) => (
    <PopoverTrigger
      popoverContent={<span>{item.label}</span>}
      triggerId={item.label}
      icon={false}
      hover
      invertedColors
      extraMarginTop={8}
      placement="right"
      popoverClass="u-rounded u-px-2 u-py-1 u-whitespace-no-wrap tooltip-nav-link u-ml-20">
      <i className={item.icon} />
    </PopoverTrigger>
  );

  renderExpandedLink = (item) => (
    <div className="u-flex u-items-center u-flex-1 u-min-w-0">
      <i className={item.icon} />
      <span className="u-truncate">{item.label}</span>
    </div>
  );

  toggleDropdown = (item) => () => {
    this.setState((prevState) => {
      const open = {};
      open[item.label] = !prevState.open[item.label];
      return { open };
    });
  };

  renderItem = (item) => {
    if (item.type === 'dropdown') {
      const activeChild = item.items.some((i) => i.active);
      return (
        <>
          {this.state.collapsed && (
            <span href={item.url} className={`navbar-nav-link ${item.active ? 'active' : ''}`}>
              {this.renderCollapsedLink(item)}
            </span>
          )}
          {!this.state.collapsed && (
            <span onClick={this.toggleDropdown(item)} className="navbar-nav-link">
              {this.renderExpandedLink(item)}
              <i
                className={`u-text-white u-ml-2 Icon Icon-chevron-${
                  item.active || activeChild || this.state.open[item.label] ? 'down' : 'right'
                }`}
              />
            </span>
          )}
          {(activeChild || this.state.open[item.label]) && (
            <div className="u-bg-union-highlight">{item.items.map(this.renderItem)}</div>
          )}
        </>
      );
    }
    if (item.type === 'link') {
      return (
        <a href={item.url} className={`navbar-nav-link ${item.active ? 'active' : ''}`}>
          {this.state.collapsed && this.renderCollapsedLink(item)}
          {!this.state.collapsed && this.renderExpandedLink(item)}
        </a>
      );
    }
    if (item.type === 'separator') {
      return <span className="navbar-nav-separator" />;
    }
  };

  render() {
    const { collapsed } = this.state;
    const navbarCollapseClass = collapsed ? 'navbar--collapsed' : 'navbar--expanded';
    return (
      <div
        className={`navbar ${
          this.props.isStaff ? 'navbar--staff-nav' : 'navbar--community-nav'
        } ${navbarCollapseClass}`}>
        <div className="u-block lg:u-hidden">
          <span className="Modal-close Modal-dismiss u-z-50" onClick={this.handleMobileCloseClick}>
            <i className="Icon Icon-close u-text-grey u-text-lg" />
          </span>
        </div>
        <OrgCommunitySelector
          organizations={this.props.organizations}
          navbarCollapsed={this.state.collapsed}
          unionLogo={this.props.unionLogo}
          newCommunityUrl={this.props.newCommunityUrl}
          isStaff={this.props.isStaff}
          canOpenSelector={this.props.canOpenSelector}
          activeCommunity={this.props.activeCommunity}
        />
        <div className="u-hidden lg:u-block">
          <span className="navbar-collapse-trigger" onClick={this.handleCollapseTriggerClick}>
            <i
              className={`Icon u-m-0 u-text-white ${
                this.state.collapsed ? 'Icon-chevron-right' : 'Icon-chevron-left'
              }`}
            />
          </span>
        </div>
        <ul className="navbar-nav">
          {this.props.nav.items.map((item, index) => (
            <li key={index} data-item-type={item.type} data-item-label={item.label}>
              {this.renderItem(item)}
            </li>
          ))}
        </ul>
        <div className="navbar-union-logo">
          <img
            src={this.props.unionLogo}
            alt=""
            className={`u-h-4 ${this.state.collapsed ? '' : 'u-mr-6'}`}
          />
          {!this.state.collapsed && (
            <span>
              Powered by&nbsp;
              <span className="u-font-semibold">Union</span>
            </span>
          )}
        </div>
      </div>
    );
  }
}
